import { DateFormats, RtdClientChangesNotification } from "intake_shared/src/types";
import * as React from "react";
import moment from "../../../../utils/app_moment";
import { orderBy } from "lodash";
import { httpsCallable } from "firebase/functions";
import {
    CLIENT_TAGS,
    CLIENT_TAG_AUTOMATION_USER,
    canManageClientTags,
    ClientTagActivity,
    ClientTagHistoryPage,
    ClientTagsRequest,
} from "intake_shared/src/client_tags";
import { AclContext } from "../../../../contexts/AclContext";
import { fxs } from "../../../../utils/firestore";
import { Button } from "../../bits/Button/Button";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";

const requestTagHistory = httpsCallable<ClientTagsRequest, ClientTagHistoryPage>(
    fxs,
    "httpFunctionClientTags"
);

function NotificationHistoryCard({
    notification,
}: {
    notification: RtdClientChangesNotification;
}) {
    return (
        <div>
            <div>
                <p>
                    {moment(notification.notification_datetime).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}{" "}
                    by {notification.actioned_by}
                </p>
                <div>
                    <ul>
                        {notification.notification_msg
                            .split("\n")
                            .map((msg, idx) =>
                                msg ? (
                                    <li key={`notficiation-li-${idx}`}>{msg}</li>
                                ) : null
                            )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function ClientChangeHistoryNotification({
    notifications,
    clientId,
    firstName,
    showPopup,
    onClose,
}: {
    notifications: RtdClientChangesNotification[];
    clientId: string;
    showPopup: boolean;
    firstName: string;
    onClose: () => void;
}) {
    const allowed = canManageClientTags(React.useContext(AclContext)?.authUser?.acl);
    const [activities, setActivities] = React.useState<ClientTagActivity[]>([]);
    const [cursor, setCursor] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(allowed);
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        if (!allowed) return;
        let cancelled = false;
        requestTagHistory({ action: "history", clientId })
            .then(({ data }) => {
                if (cancelled) return;
                setActivities(data.activities);
                setCursor(data.nextCursor);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [clientId, allowed]);

    const loadMore = async () => {
        setLoading(true);
        setError(false);
        try {
            const { data } = await requestTagHistory({
                action: "history",
                clientId,
                afterActivityId: cursor,
            });
            setActivities((previous) =>
                cursor ? [...previous, ...data.activities] : data.activities
            );
            setCursor(data.nextCursor);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const tagNotifications: RtdClientChangesNotification[] = allowed
        ? activities.map((entry) => ({
              id: `tag-${entry.client_tag_activity_id}`,
              client_id: clientId,
              actioned_by: entry.author.name,
              notification_datetime: moment(entry.created_at).valueOf(),
              notification_msg: `${
                  entry.kind === "automatic" ||
                  entry.author.email === CLIENT_TAG_AUTOMATION_USER.email
                      ? entry.action === "added"
                          ? "Automatically added"
                          : "Automatically removed"
                      : entry.action === "added"
                      ? "Added"
                      : "Removed"
              } tag: ${
                  CLIENT_TAGS.find((tag) => tag.key === entry.tag_key)?.label ??
                  entry.tag_key
              } (${entry.origin_system === "mantle" ? "Mantle" : "Intake"})`,
          }))
        : [];
    const history = orderBy(
        [...notifications.slice(-50), ...tagNotifications],
        ["notification_datetime"],
        ["desc"]
    );

    return (
        <AlertDialog
            open={showPopup}
            onClose={onClose}
            onSaveClick={onClose}
            title={`Change history for ${firstName}`}
            description={
                <div>
                    {history.map((notification) => (
                        <NotificationHistoryCard
                            key={notification.id}
                            notification={notification}
                        />
                    ))}
                    {!history.length && !loading && !error && (
                        <p>No change notification for this client</p>
                    )}
                    {allowed && loading && <p role="status">Loading tag history…</p>}
                    {allowed && error && (
                        <p role="alert">
                            Unable to load tag history.{" "}
                            <Button
                                variant="normal"
                                onClick={loadMore}
                                disabled={loading}
                            >
                                Retry
                            </Button>
                        </p>
                    )}
                    {allowed && cursor && !error && (
                        <Button
                            variant="normal"
                            onClick={loadMore}
                            disabled={loading}
                        >
                            Load more tag history
                        </Button>
                    )}
                </div>
            }
            showCancelButton={false}
            saveText="Close"
        />
    );
}

export default ClientChangeHistoryNotification;
